
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface BlogGeneratorFormValues {
  topic: string;
  keywords: string;
  wordCount: number;
}

interface BlogGeneratorProps {
  onBlogGenerated: (content: string, topic: string, keywords: string) => void;
}

const BlogGenerator: React.FC<BlogGeneratorProps> = ({ onBlogGenerated }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<BlogGeneratorFormValues>({
    defaultValues: {
      topic: '',
      keywords: 'IT management, cybersecurity, technology solutions',
      wordCount: 1500
    }
  });

  const generateBlogContent = async (data: BlogGeneratorFormValues) => {
    setIsGenerating(true);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-blog-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          topic: data.topic,
          keywords: data.keywords,
          wordCount: data.wordCount
        }),
      });

      const result = await response.json();
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      toast.success('Blog content generated successfully!');
      onBlogGenerated(result.content, result.topic, result.keywords);
    } catch (error) {
      console.error('Error generating blog content:', error);
      toast.error(`Failed to generate blog content: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Generate Blog Content</CardTitle>
        <CardDescription>
          Use AI to generate a blog post with proper structure, headings, and keywords.
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(generateBlogContent)}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="topic">Blog Topic</Label>
            <Input
              id="topic"
              placeholder="Cybersecurity Best Practices for Small Businesses"
              {...register('topic', { required: 'Topic is required' })}
            />
            {errors.topic && <p className="text-sm text-red-500">{errors.topic.message}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="keywords">Keywords (comma separated)</Label>
            <Input
              id="keywords"
              placeholder="IT management, cybersecurity, technology solutions"
              {...register('keywords')}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="wordCount">Word Count</Label>
            <Input
              id="wordCount"
              type="number"
              min={500}
              max={2000}
              step={100}
              {...register('wordCount', { 
                valueAsNumber: true,
                min: { value: 500, message: 'Minimum 500 words' },
                max: { value: 2000, message: 'Maximum 2000 words' }
              })}
            />
            {errors.wordCount && <p className="text-sm text-red-500">{errors.wordCount.message}</p>}
          </div>
        </CardContent>
        <CardFooter>
          <Button type="submit" disabled={isGenerating}>
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              'Generate Blog Post'
            )}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default BlogGenerator;
